import React, { useState, useMemo, useEffect, useRef } from 'react';
import './select-cart-item.scss';
import { ButtonVariant, Form } from '@ampol/design-system/lib';
import CardImage from '../../../../components/card-image/card-image';
import QuantityField from '../../../../components/quantity-field/QuantityField';
import RoundButtonIcon from '../../../../components/round-button-icon/RoundButtonIcon';
import * as yup from 'yup';
import { string } from 'yup';
import { currencyFormat } from '../../../../utilities/core-utilities';
import { isDateBetween } from '../../../../utilities/dayjs-utilities';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { DATE_FORMATS } from 'utilities/constants';
import Validation from 'constants/validation-messages';
import Regex from 'constants/regex';
import { ReadonlyField, useModal } from '@ampol/design-system';
import { getExtrasList, resolveAllowedExtras } from 'features/ampol-cash/helpers/cash-order-helper';

/**
 * Cart Item Row Order Component
 * @param cartItem Used to set default values on edit of cart item row
 * @param defaultPlasticTypeCode Default plastic code for card image
 * @param denominationList List of denomination values in Amount dropdown
 * @param plasticTypesList List of plastic types
 * @param onAdd Parent function to pass new Cart Item object to
 * @param onEdit Parent function to pass edited Cart Item object to
 * @param onRemove Parent function to pass Cart Item object to remove
 * @param isAdmin Flag indicating if account is admin
 * @param preloaded Flag for whether acc classifier is preloaded
 * @param defaultExpiryDate Default date
 * @param onEditStateChange Parent function to pass form state change isValid flag
 * @param fleetLimitters
 * @returns JSX Element
 */

const SelectCartItem = ({
  cartItem,
  defaultPlasticTypeCode,
  denominationList,
  plasticTypesList,
  onAdd,
  onEdit,
  onEditMediaType,
  onRemove,
  isAdmin,
  preloaded,
  defaultExpiryDate,
  onEditStateChange,
  defaultMediaType,
  allowedMedia,
  fleetLimitters,
}) => {
  const {
    mediaType,
    denomination,
    denominationCode,
    quantity = '',
    uuid,
    expiryDate,
    plasticTypeCode: currentPlasticTypeCode,
    plasticTypeSource,
  } = cartItem || {};

  const cartItemFormId = `cart-item-form${uuid ? `-${uuid}` : ''}`;

  const isInitialMount = useRef(true);

  const [qtyField, setQtyField] = useState(quantity);
  const qtyRef = useRef(qtyField);
  const [qtyFieldMsg, setQtyFieldMsg] = useState('');
  const [isFormValid, setIsFormValid] = useState(!!onEdit);
  const [itemId, setItemId] = useState(uuid || uuidv4());

  const [plasticType, setPlasticType] = useState();
  const [plasticTypeCode, setPlasticTypeCode] = useState(currentPlasticTypeCode ?? defaultPlasticTypeCode);

  // State values for updating media type in edit cart item
  const [updateMediaType, setUpdateMediaType] = useState(false);
  const [updatedDefaultValues, setUpdatedDefaultValues] = useState({});

  useEffect(() => {
    setPlasticTypeCode(currentPlasticTypeCode || defaultPlasticTypeCode);
  }, [currentPlasticTypeCode, defaultPlasticTypeCode]);

  useEffect(() => {
    setPlasticType(plasticTypesList?.find((p) => p.id === plasticTypeCode));
  }, [plasticTypesList, plasticTypeCode]);

  // Validate quantity field on update (and not initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      validateQuantity(qtyField);
      qtyRef.current = qtyField;

      onEdit && onEdit(createCartItem(cartItem, qtyField));
    }
  }, [qtyField]);

  useEffect(() => {
    onEdit && onEditStateChange(cartItem.uuid, !qtyFieldMsg && isFormValid);
  }, [qtyFieldMsg, isFormValid]);

  const isButtonActive = isFormValid && qtyField !== '' && qtyField % 1 === 0 && qtyField <= 4000 && plasticType;

  const mediaTypes = allowedMedia?.map((media) => {
    return {
      label: media,
      value: media.charAt(0),
    };
  });

  const getDefaultMediaType = () => {
    if (mediaType) return mediaType;

    if (defaultMediaType) return defaultMediaType;

    if (mediaTypes?.length === 1) return mediaTypes[0]?.value;

    if (mediaTypes?.length === 2 && mediaTypes?.find((mediaType) => mediaType.value === 'P')) return 'P';

    return null;
  };

  const fleetLimittersList = useMemo(
    () =>
      typeof fleetLimitters === 'string'
        ? fleetLimitters.split(';').map((item) => item.trim())
        : Array.isArray(fleetLimitters)
          ? fleetLimitters
          : [],
    [fleetLimitters]
  );
  /**
   * Returns the denomination that should be used by the extras policy.
   * Admin/preloaded cards use the entered denomination.
   * Other cards use denominationCode.
   */
  const getEffectiveDenomination = (values = {}) => (isAdmin ? values?.denomination : values?.denominationCode);

  const initialValues = useMemo(() => {
    const initialMediaType = getDefaultMediaType();

    const effectiveDenomination = isAdmin ? denomination : denominationCode;

    const { defaultExtras } = resolveAllowedExtras({
      mediaType: initialMediaType,
      denomination: effectiveDenomination,
      fleetLimitters: fleetLimittersList,
    });

    return {
      mediaType: initialMediaType,
      denominationCode: isAdmin && preloaded ? 0 : denominationCode == 0 ? '' : denominationCode,
      denomination: denomination || '',
      extras: cartItem?.extras ?? defaultExtras,
      expiryDate: expiryDate || defaultExpiryDate,
    };
  }, [
    cartItem?.extras,
    mediaType,
    defaultMediaType,
    denominationCode,
    denomination,
    expiryDate,
    defaultExpiryDate,
    isAdmin,
    preloaded,
    fleetLimittersList,
  ]);

  const [defaultValues, setDefaultValues] = useState(initialValues);

  useEffect(() => {
    if (initialValues) {
      setDefaultValues(initialValues);
    }
  }, [initialValues]);

  const validationSchema = yup.object().shape({
    mediaType: string().nullable().required(Validation.MEDIA_TYPE_REQUIRED),
    ...(isAdmin && preloaded
      ? {
        denomination: yup
          .number()
          .integer(Validation.INTEGER)
          .min(1, Validation.DENOMINATION_MIN)
          .max(4500, Validation.DENOMINATION_MAX)
          .nullable(true)
          .transform((_, val) => (val ? Number(val) : null))
          .required(Validation.DENOMINATION_REQUIRED),
      }
      : {}),
    denominationCode: string().nullable().required(Validation.DENOMINATION_CODE_REQUIRED),
    expiryDate: string()
      .nullable()
      .required(Validation.EXPIRY_DATE_REQUIRED)
      .matches(Regex.EXPIRY_MONTH_YEAR, {
        message: Validation.EXPIRY_DATE_MONTH_YEAR,
        excludeEmptyString: true,
      })
      .test('expiryDate', Validation.EXPIRY_DATE_RANGE, (value) => {
        if (!value.match(Regex.EXPIRY_MONTH_YEAR)) {
          return true;
        }
        const inputDate = dayjs(`01/${value}`, DATE_FORMATS).endOf('month');

        const minDate = dayjs().endOf('month');
        const maxDate = dayjs().add(100, 'month').startOf('month');

        // Valid date is within 1 to 99 months from today's month
        return isDateBetween(inputDate, minDate, maxDate);
      }),
  });

  const createCartItem = (obj, quantity) => {
    return {
      ...obj,
      uuid: onAdd ? itemId : cartItem.uuid,
      isFuelOnly: fleetLimittersList.includes('Wet Stock') ? true : obj.extras?.includes('fuel'),
      verificationType: obj.extras?.some((x) => x === 'pin') ? 'P' : 'N',
      quantity: quantity,
      denominationCode: obj.denominationCode || 0,
      total: preloaded ? (isAdmin ? quantity * obj.denomination : quantity * obj.denominationCode) : 0,
    };
  };

  const doEdit = (item) => {
    onEdit && onEdit(createCartItem(item, qtyRef.current));
  };

  const updateMediaModal = useModal({
    id: 'update-media-modal',
    title: 'Are you sure',
    content: 'Switching AmpolCash card type will apply to the entire order.',
    color: 'blue',
    actions: [
      {
        label: 'OK',
        onClick: () => {
          setUpdateMediaType(true);
          updateMediaModal.close();
        },
        variant: ButtonVariant.Primary,
      },
      {
        label: 'Cancel',
        onClick: () => {
          updateMediaModal.close();
        },
      },
    ],
    hideCloseButton: true,
  });

  const handleSubmit = (obj) => {
    if (!isButtonActive) {
      return;
    }

    const newCartItem = {
      ...obj,
      plasticTypeCode: plasticType?.id,
      plasticTypeSource: plasticType?.image?.url,
    };

    onAdd && onAdd(createCartItem(newCartItem, qtyField));

    // Reset to initial values
    setQtyField('');
    setPlasticTypeCode(defaultPlasticTypeCode);
    setItemId(uuidv4());
    if (defaultMediaType !== null) {
      setDefaultValues({ ...initialValues });
    }
    setQtyFieldMsg('');
  };

  const handleStateChange = (obj) => {
    setIsFormValid(obj.isValid);
  };

  // Use effect to ensure media type is only updated when user clicks OK in updateMediaModal
  useEffect(() => {
    if (updateMediaType) {
      const values = onEditMediaType ? updatedDefaultValues : defaultValues;

      const effectiveDenomination = getEffectiveDenomination(values);

      const { allowedExtras } = resolveAllowedExtras({
        mediaType: values?.mediaType,
        denomination: effectiveDenomination,
        fleetLimitters: fleetLimittersList,
      });

      const extrasFiltered = values?.extras?.filter((extra) => allowedExtras.some((item) => item.value === extra));

      const updatedObj = { ...values, extras: extrasFiltered };

      setDefaultValues(updatedObj);
      setUpdateMediaType(false);

      onEditMediaType && onEditMediaType(updatedDefaultValues.mediaType);
    }
  }, [updateMediaType, updatedDefaultValues, defaultValues, fleetLimittersList, isAdmin]);

  const handleOnChange = (obj, name) => {
    let updatedObj = obj;

    if (name === 'mediaType') {
      if (cartItem) {
        setUpdatedDefaultValues(updatedObj);
        updateMediaModal.open();
        return;
      } else {
        setUpdateMediaType(true);
      }
    }

    if (name === 'denominationCode' || name === 'denomination' || name === 'mediaType') {
      const effectiveDenomination = getEffectiveDenomination(updatedObj);

      const { defaultExtras } = resolveAllowedExtras({
        mediaType: updatedObj?.mediaType,
        denomination: effectiveDenomination,
        fleetLimitters: fleetLimittersList,
      });

      updatedObj = {
        ...updatedObj,
        extras: defaultExtras,
      };
    }

    if (!preloaded && name === 'denominationCode') {
      const selectedDenomination = denominationList.find((d) => d.code === updatedObj.denominationCode);
      setPlasticTypeCode(selectedDenomination.plasticTypeCode);

      const newPlasticType = plasticTypesList.find((item) => item.id === selectedDenomination.plasticTypeCode);

      doEdit({
        ...cartItem,
        ...updatedObj,
        plasticTypeCode: newPlasticType?.id,
        plasticTypeSource: newPlasticType?.image?.url,
      });
    } else {
      doEdit({ ...cartItem, ...updatedObj });
    }

    setDefaultValues({ ...initialValues, ...updatedObj });
  };

  const validateQuantity = (val) => {
    if (val === '') {
      setQtyFieldMsg(Validation.QUANTITY_MSG_REQUIRED);
    } else if (val % 1 !== 0) {
      setQtyFieldMsg(Validation.INTEGER);
    } else if (val > 4000) {
      setQtyFieldMsg(Validation.QUANTITY_MAX);
    } else {
      setQtyFieldMsg('');
    }
  };

  return (
    <>
      <Form
        id={cartItemFormId}
        schema={validationSchema}
        onSubmit={handleSubmit}
        defaultValues={{ ...defaultValues }}
        onStateChange={handleStateChange}
        onChange={handleOnChange}
        validationMode='onChange'
        HTMLFormProps={{ noValidate: true }}
      >
        <div className={'cart-row'}>
          <div className='cart-row__card-image'>
            <CardImage
              isLarge={true}
              isDigital={defaultValues?.mediaType === 'D'}
              imageSource={plasticTypeSource ?? plasticType?.image?.url}
              useCacheImage={true}
            />
          </div>
          <div className={'cart-row__media-type'}>
            <Form.DropdownSearch
              label={'Card Type'}
              labelType={'hidden'}
              name='mediaType'
              type='dropdown'
              placeholder='Select type'
              id={'cart-item-media-type'}
              data={mediaTypes}
              allowClear={false}
              disabled={onAdd && !!defaultMediaType && mediaTypes.length > 1}
            />
            {defaultMediaType && mediaTypes.length > 1 && (
              <div className='cart-row__media-type-text'>
                <div>Orders are limited to one card type.</div>
                <div>Create a new order to select a different card type.</div>
              </div>
            )}
          </div>
          {isAdmin && preloaded && (
            <Form.Input
              name='denomination'
              label={'Denomination'}
              labelType={'hidden'}
              placeholder='Denomination'
              id={'cart-item-denomination'}
              type='number'
              min={1}
              step='any'
              onKeyDown={(evt) => ['e', 'E', '+', '-', '.'].includes(evt.key) && evt.preventDefault()}
            />
          )}
          {!(isAdmin && preloaded) && (
            <Form.DropdownSearch
              label={'Denomination'}
              labelType={'hidden'}
              name='denominationCode'
              id={'cart-item-denomination-code'}
              type='dropdown'
              placeholder='Denomination'
              allowClear={false}
              listHeight={220}
              data={denominationList
                ?.filter((d) => d.code !== '0')
                .map((d) => ({
                  value: d.code,
                  label: currencyFormat(d.code),
                }))}
            />
          )}
          <QuantityField
            value={quantity}
            quantity={qtyField}
            setQuantity={setQtyField}
            quantityMessage={qtyFieldMsg}
            className={'cart-row--quantity-mobile'}
            max={4001}
          />

          {defaultValues?.mediaType === 'D' && fleetLimittersList.includes('Wet Stock') ? (
            <div>
              <ReadonlyField type='dropdown' value={'Fuel only'} />
            </div>
          ) : (
            <Form.DropdownSearch
              label={'Extras'}
              labelType={'hidden'}
              name='extras'
              type='dropdown'
              placeholder='Extras'
              id={'cart-item-extras'}
              data={getExtrasList(
                defaultValues?.mediaType === 'D',
                getEffectiveDenomination(defaultValues),
                fleetLimittersList
              )}
              selectMultiple={true}
              showFooter={false}
              maxTagCount='responsive'
              allowClear={false}
              filterOptions={false}
              searchOptions={false}
            />
          )}

          {isAdmin && preloaded && (
            <Form.Input
              name='expiryDate'
              placeholder={'MM/YY'}
              label={'Expiry Date'}
              labelType={'hidden'}
              id={'cart-item-expiry-date'}
            />
          )}
          <div className='cart-row__button'>
            <span className='cart-row__button-text'>Add card/s</span>
            <RoundButtonIcon
              plusIcon={onAdd}
              buttonColor={onAdd ? 'blue' : 'red'}
              iconColor={'white'}
              inactive={onAdd && !isButtonActive}
              alt={'description'}
              formId={onAdd && (!isButtonActive ? undefined : cartItemFormId)}
              type={onAdd ? 'submit' : 'button'}
              onClick={onRemove ? () => onRemove(cartItem) : () => !isButtonActive && validateQuantity(qtyField)}
            />
          </div>
        </div>
      </Form>
    </>
  );
};

export default SelectCartItem;
